import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Eye, Heart, Check, Search, ShoppingCart, Truck, BadgeCheck, ChevronRight } from 'lucide-react';

const AboutPage = () => {
  return (
    <main>
      {/* Hero Banner */}
      <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1600&auto=format&fit=crop&q=80"
            alt="Montana farmland"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#3B2F1E]/90 via-[#3B2F1E]/50 to-[#2D5016]/30" />
        </div>
        <div className="relative z-10 text-center px-4">
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
            About Good Grains
          </h1>
          <Link
            to="/contact"
            className="inline-block px-8 py-3 rounded-lg bg-[#A0522D] text-white font-semibold hover:bg-[#8B4513] transition-colors"
          >
            Contact Us
          </Link>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-[#FAF6F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block px-3 py-1 rounded-full bg-[#2D5016]/10 text-[#2D5016] text-xs font-semibold uppercase tracking-wider mb-4">
              Our Journey
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#3B2F1E] mb-4">
              Our Story
            </h2>
            <p className="text-[#3B2F1E]/70 text-lg max-w-2xl mx-auto">
              Healthy animals start with feed you can trust, and that's exactly what we provide.
            </p>
          </div>

          {/* Story Part 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <p className="text-[#3B2F1E]/80 leading-relaxed mb-6">
                At Good Grains, we know that caring for your flock, herd, or farm animals isn't always easy. That's why we're here to make it simple with high-quality, Non-GMO feed, delivered with honesty, care, and community in mind. Good Grains is a family business built on faith, hard work, and love for animals.
              </p>
              <p className="text-[#3B2F1E]/80 leading-relaxed mb-6">
                Robert and Gwen Norton were high school sweethearts who celebrated their 50th wedding anniversary this past year. Their life together has always been rooted in faith, family, and hard work.
              </p>
              <p className="text-[#3B2F1E]/80 leading-relaxed">
                They raised seven children, six sons and one daughter, on a small homesteading farm in rural Michigan. Gwen home-birthed five of those babies while tending gardens, milking goats, raising chickens, pigs, and teaching children what it means to live with purpose in a close-knit, faith-based community.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_prairie-grains/artifacts/6cskqzct_image.jpeg"
                alt="Bobby milking Heidi the goat at age 8"
                className="w-full h-[400px] object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-4 -left-4 w-full h-full border-4 border-[#D4A84B] rounded-2xl -z-10" />
              {/* Caption */}
              <div className="absolute bottom-4 left-4 right-4 bg-[#3B2F1E]/80 backdrop-blur-sm rounded-xl p-4">
                <p className="text-white text-sm italic text-center">
                  At our Michigan hobby farm in 1983. Our first born son Bobby, milking Heidi the goat, at the age of 8.
                </p>
              </div>
            </div>
          </div>

          {/* Story Part 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <div className="relative order-2 lg:order-1">
              {/* Side by side photos of Robert and Gwen */}
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_prairie-grains/artifacts/ytajinv1_image.png"
                  alt="Robert and Gwen Norton"
                  className="w-full h-[380px] object-cover rounded-2xl shadow-2xl"
                />
                <img
                  src="https://customer-assets.emergentagent.com/job_prairie-grains/artifacts/ler4e127_image.png"
                  alt="Robert and Gwen Norton"
                  className="w-full h-[380px] object-cover rounded-2xl shadow-2xl"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-full h-full border-4 border-[#2D5016] rounded-2xl -z-10" />
            </div>
            <div className="order-1 lg:order-2">
              <p className="text-[#3B2F1E]/80 leading-relaxed mb-6">
                Robert built and ran an underground utility contracting business, always instilling the value of diligence, responsibility, and service. Gwen's guiding motto was simple but powerful: live honorably, be kind, and help others whenever you can. Decades later, their legacy has grown to include 29 grandchildren and two great-grandchildren. Today, they are lovingly known as Meme and Papa.
              </p>
              <p className="text-[#3B2F1E]/80 leading-relaxed mb-6">
                In March of 2021, after serving a two-year mission for their church in Micronesia, Guam — Robert and Gwen felt called to a new chapter. They moved to the Lower Valley in Kalispell, Montana, settling on five acres next door to their son's 27-acre hobby farm.
              </p>
              <p className="text-[#3B2F1E]/80 leading-relaxed">
                Gwen launched Wind Chime Farms, pouring her passion into cultivating beautiful cut flowers and thriving vegetable gardens in the fertile valley soil. Meanwhile, Robert, drawing on a lifetime of caring for livestock, began searching for the kind of high-quality, grain-based animal feed he could truly stand behind.
              </p>
            </div>
          </div>

          {/* Story Part 3 */}
          <div className="mt-8">
            <p className="text-[#3B2F1E]/80 leading-relaxed mb-8">
              That search led him to Michelle Dilsaver of Holy Cross Farms. In May of 2025, Robert purchased the Holy Cross Farms feed business and founded Good Grains with a clear mission:
            </p>
            <blockquote className="border-l-4 border-[#D4A84B] pl-8 py-6 my-8 bg-[#2D5016]/5 rounded-r-xl">
              <p className="text-[#2D5016] text-xl md:text-2xl font-serif italic leading-relaxed">
                To provide quality animal feed you can trust, reliable service you can count on, and genuine relationships built to last.
              </p>
            </blockquote>
            <p className="text-[#3B2F1E]/80 leading-relaxed">
              Good Grains is more than a feed company; it is the continuation of a family legacy built on faith, integrity, hard work, and caring deeply for the people and animals we serve.
            </p>
          </div>
        </div>
      </section>

      {/* Mission / Goal / Vision */}
      <section className="py-24 bg-[#2D5016]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white/10 rounded-2xl p-8 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-colors">
              <Target className="w-12 h-12 text-[#D4A84B] mb-6" />
              <h3 className="font-serif text-2xl font-bold text-white mb-4">Our Mission</h3>
              <p className="text-white/80 leading-relaxed">
                To provide quality animal feed you can trust, reliable service you can count on, and genuine relationships built to last.
              </p>
            </div>
            <div className="bg-white/10 rounded-2xl p-8 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-colors">
              <Heart className="w-12 h-12 text-[#D4A84B] mb-6" />
              <h3 className="font-serif text-2xl font-bold text-white mb-4">Our Goal</h3>
              <p className="text-white/80 leading-relaxed">
                To ease your day and help your animals thrive.
              </p>
            </div>
            <div className="bg-white/10 rounded-2xl p-8 backdrop-blur-sm border border-white/20 hover:bg-white/15 transition-colors">
              <Eye className="w-12 h-12 text-[#D4A84B] mb-6" />
              <h3 className="font-serif text-2xl font-bold text-white mb-4">Our Vision</h3>
              <p className="text-white/80 leading-relaxed">
                To bring wholesome, nourishing grains to every family.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* We Live This Mission By */}
      <section className="py-24 bg-[#FAF6F0]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl font-bold text-[#3B2F1E] mb-4">
              We Live This Mission By
            </h2>
          </div>
          <div className="space-y-4">
            {[
              'Communicating clearly and being upfront about pricing',
              'Making ordering and delivery simple',
              'Treating every customer like part of our homestead family',
              'We only offer feed we trust and use ourselves'
            ].map((item) => (
              <div key={item} className="flex items-center gap-4 bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
                <div className="w-10 h-10 rounded-full bg-[#2D5016] flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <p className="text-[#3B2F1E] font-medium">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do & Why It Matters */}
      <section className="py-24 bg-[#FEFCF8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-[#D4A84B]/20 text-[#A0522D] text-xs font-semibold uppercase tracking-wider mb-4">
                What We Do
              </span>
              <h2 className="font-serif text-4xl font-bold text-[#3B2F1E] mb-6">
                What We Do & Why It Matters
              </h2>
              <p className="text-[#3B2F1E]/80 leading-relaxed mb-6">
                We supply all types of animal feed, organic and non-GMO animal feed. Our feed supports the growth, health, and well-being of chickens, goats, cows, pigs, rabbits, horses, and more.
              </p>
              <p className="text-[#3B2F1E]/80 leading-relaxed">
                By providing dependable, wholesome feed, we make life easier for homesteaders, small farms, and eco-conscious farmers, so they can spend more time caring for their animals and less time worrying about their feed.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1530836369250-ef72a3f5cda8?w=800&auto=format&fit=crop&q=80"
                alt="Farm animals"
                className="w-full h-[400px] object-cover rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-24 bg-[#2D5016]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-bold text-[#3B2F1E] mb-4">
              Who We Serve
            </h2>
            <p className="text-[#3B2F1E]/70 text-lg max-w-2xl mx-auto">
              If you care about the quality of what your animals eat, Good Grains is here for you. We understand your needs because we've lived them ourselves.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: 'Backyard Flocks', desc: 'Small-scale farmers and backyard enthusiasts' },
              { title: 'Growing Farms', desc: 'Expanding operations and larger farms' },
              { title: 'Eco-Friendly Farmers', desc: 'Homesteaders and environmentally conscious farmers' }
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-2xl p-8 shadow-lg text-center hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 rounded-full bg-[#D4A84B]/20 flex items-center justify-center mx-auto mb-6">
                  <BadgeCheck className="w-8 h-8 text-[#D4A84B]" />
                </div>
                <h3 className="font-serif text-xl font-bold text-[#3B2F1E] mb-2">{item.title}</h3>
                <p className="text-[#3B2F1E]/70">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Working With Us */}
      <section className="py-24 bg-[#FAF6F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl font-bold text-[#3B2F1E] mb-4">
              Working With Us
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { step: 1, title: 'Browse', desc: 'All types, Non-GMO, high-quality feed', icon: Search },
              { step: 2, title: 'Place your order', desc: 'Online (on the website) or by phone', icon: ShoppingCart },
              { step: 3, title: 'Receive your feed', desc: 'Local delivery in Kalispell and surrounding areas', icon: Truck },
              { step: 4, title: 'Feed with confidence', desc: 'Healthy, trusted feed for your animals', icon: BadgeCheck }
            ].map((item) => (
              <div key={item.step} className="bg-white rounded-2xl p-6 shadow-lg relative">
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#D4A84B] flex items-center justify-center text-white font-bold text-sm">
                  {item.step}
                </div>
                <item.icon className="w-10 h-10 text-[#2D5016] mb-4" />
                <h3 className="font-semibold text-[#3B2F1E] mb-2">{item.title}</h3>
                <p className="text-[#3B2F1E]/70 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Community CTA */}
      <section className="py-24 bg-[#2D5016]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-6">
            Join Our Community!
          </h2>
          <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
            Good Grains is more than a feed supplier; it's a community of farmers, homesteaders, and animal lovers. Follow us on social media, reach out with questions, or place your order today. Let's keep your animals healthy and happy together.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/shop"
              className="px-8 py-4 rounded-lg bg-[#D4A84B] text-[#3B2F1E] font-semibold hover:bg-[#c49a3d] transition-colors flex items-center gap-2"
            >
              Explore Our Products
              <ChevronRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="px-8 py-4 rounded-lg border-2 border-white/50 text-white font-semibold hover:bg-white/10 transition-colors flex items-center gap-2"
            >
              Contact Us
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
